import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

(() => {
  if (window.CartVariantSelectorLoaded) return;

  window.CartVariantSelectorLoaded = true;

  document.addEventListener(
    'change',
    handleVariantChange
  );

  async function handleVariantChange(event) {
    const select = event.target.closest(
      '[data-cart-color-selector]'
    );

    if (!select) return;

    const line =
      Number(select.dataset.line);

    const oldVariantId =
      Number(
        select.dataset.currentVariantId
      );

    const newVariantId =
      Number(select.value);

    const quantity =
      Number(
        select.dataset.quantity || 1
      );

    if (
      !line ||
      !oldVariantId ||
      !newVariantId ||
      newVariantId === oldVariantId
    ) {
      return;
    }

    const originalValue =
      String(oldVariantId);

    const lineData =
      getLineData(select);

    select.disabled = true;

    try {
      /*
       * STEP 1
       *
       * Remove the ORIGINAL line first.
       *
       * At this moment we know its line number
       * is still correct because the cart hasn't
       * been changed yet.
       */
      const removeResponse =
        await fetch(
          `${shopRoot()}cart/change.js`,

          fetchConfig(
            'json',
            {
              body: JSON.stringify({
                line: line,
                quantity: 0
              })
            }
          )
        );

      const removeData =
        await removeResponse.json();

      if (!removeResponse.ok) {
        throw new Error(
          removeData.description ||
          removeData.message ||
          'The original color could not be removed.'
        );
      }

      /*
       * STEP 2
       *
       * Build the replacement cart item.
       */
      const newItem = {
        id: newVariantId,
        quantity: quantity
      };

      /*
       * Preserve line-item properties.
       *
       * This includes the private
       * _Complete the Triad property.
       */
      if (
        lineData.properties &&
        Object.keys(
          lineData.properties
        ).length > 0
      ) {
        newItem.properties =
          lineData.properties;
      }

      /*
       * Preserve selling plan if applicable.
       */
      if (lineData.sellingPlan) {
        newItem.selling_plan =
          lineData.sellingPlan;
      }

      const sectionIds =
        getCartSectionIds();

      /*
       * STEP 3
       *
       * Add the newly selected variant and ask
       * Shopify for fresh cart sections.
       */
      const addResponse =
        await fetch(
          `${shopRoot()}cart/add.js`,

          fetchConfig(
            'json',
            {
              body: JSON.stringify({
                items: [newItem],

                sections:
                  sectionIds.join(','),

                sections_url:
                  window.location.pathname
              })
            }
          )
        );

      const addData =
        await addResponse.json();

      if (!addResponse.ok) {
        /*
         * Something unexpected happened.
         *
         * Try to put the original item back
         * before reporting the error.
         */
        await restoreOriginalItem(
          oldVariantId,
          quantity,
          lineData
        );

        throw new Error(
          addData.description ||
          addData.message ||
          'That color could not be added.'
        );
      }

      /*
       * STEP 4
       *
       * Morph the freshly rendered sections.
       */
      if (addData.sections) {
        for (
          const sectionId
          of sectionIds
        ) {
          const html =
            addData.sections[
              sectionId
            ];

          if (html) {
            morphSection(
              sectionId,
              html
            );
          }
        }
      }

      /*
       * STEP 5
       *
       * Get the final cart so the event contains
       * an accurate item count and cart state.
       */
      const finalCart =
        await getCart();

      /*
       * Notify the rest of the theme.
       */
      if (sectionIds.length) {
        document.dispatchEvent(
          new CartUpdateEvent(
            {
              ...finalCart,
              sections:
                addData.sections
            },

            sectionIds[0],

            {
              itemCount:
                finalCart.item_count,

              source:
                'cart-variant-selector',

              sections:
                addData.sections
            }
          )
        );
      }

    } catch (error) {
      console.error(
        '[Cart Color Selector]',
        error
      );

      /*
       * If the original select still exists,
       * restore its visual state.
       */
      if (select.isConnected) {
        select.value =
          originalValue;

        select.disabled =
          false;
      }

      alert(
        error.message ||
        'That color could not be changed. Please try again.'
      );
    }
  }

  async function restoreOriginalItem(
    variantId,
    quantity,
    lineData
  ) {
    try {
      const item = {
        id: variantId,
        quantity: quantity
      };

      if (
        lineData.properties &&
        Object.keys(
          lineData.properties
        ).length > 0
      ) {
        item.properties =
          lineData.properties;
      }

      if (lineData.sellingPlan) {
        item.selling_plan =
          lineData.sellingPlan;
      }

      await fetch(
        `${shopRoot()}cart/add.js`,

        fetchConfig(
          'json',
          {
            body: JSON.stringify({
              items: [item]
            })
          }
        )
      );

    } catch (error) {
      console.error(
        '[Cart Color Selector] Could not restore original item.',
        error
      );
    }
  }

  async function getCart() {
    const response =
      await fetch(
        `${shopRoot()}cart.js`,
        {
          headers: {
            Accept:
              'application/json'
          },

          cache:
            'no-store'
        }
      );

    if (!response.ok) {
      throw new Error(
        'Could not refresh the cart.'
      );
    }

    return response.json();
  }

  function getLineData(select) {
  const wrapper =
    select.closest(
      '[data-cart-variant-wrapper]'
    );

  const script =
    wrapper?.querySelector(
      '[data-cart-variant-line-data]'
    );

  if (!script) {
    return {
      properties: {},
      sellingPlan: null
    };
  }

  try {
    const data =
      JSON.parse(script.textContent);

    return {
      properties:
        normalizeProperties(
          data.properties
        ),

      sellingPlan:
        data.sellingPlan || null
    };

  } catch (error) {
    console.warn(
      '[Cart Color Selector] Invalid line data.',
      error
    );

    return {
      properties: {},
      sellingPlan: null
    };
  }
}
function normalizeProperties(properties) {
  /*
   * No properties.
   */
  if (!properties) {
    return {};
  }

  /*
   * Already a normal Shopify properties object.
   *
   * Example:
   * {
   *   "_Complete the Triad": "Yes"
   * }
   */
  if (
    typeof properties === 'object' &&
    !Array.isArray(properties)
  ) {
    return properties;
  }

  /*
   * Shopify/Liquid may serialize properties
   * as an array of key/value pairs.
   *
   * Example:
   * [
   *   ["_Complete the Triad", "Yes"]
   * ]
   *
   * Convert that into the hash expected
   * by /cart/add.js.
   */
  if (Array.isArray(properties)) {
    const result = {};

    properties.forEach(property => {
      if (
        Array.isArray(property) &&
        property.length >= 2
      ) {
        result[property[0]] =
          property[1];

        return;
      }

      /*
       * Extra fallback in case Liquid returns
       * pair objects rather than two-item arrays.
       */
      if (
        property &&
        typeof property === 'object'
      ) {
        if (
          'first' in property &&
          'last' in property
        ) {
          result[property.first] =
            property.last;
        }
      }
    });

    return result;
  }

  return {};
}

  function getCartSectionIds() {
    const ids =
      new Set();

    document
      .querySelectorAll(
        'cart-items-component[data-section-id]'
      )
      .forEach(component => {
        const sectionId =
          component.dataset.sectionId;

        if (sectionId) {
          ids.add(sectionId);
        }
      });

    return Array.from(ids);
  }

  function shopRoot() {
    return (
      window.Shopify?.routes?.root ||
      '/'
    );
  }
})();